import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import railsApiService from 'services/api/rails-api-service'
import { StorefrontCart } from 'services/api/storefront-api-service'
import { normalizeJsonApi } from 'utils/normalizeJsonApi'

export interface AcademyState {
  isLoading: boolean
  programData: ProgramData | null
  enrollmentData: EnrollmentData | null
  activeLesson: ProgramLesson | null
  showPaymentForm: boolean
}

const initialState: AcademyState = {
  isLoading: false,
  programData: null,
  enrollmentData: null,
  activeLesson: null,
  showPaymentForm: false
}

export const academySlice = createSlice({
  name: 'academy',
  initialState,
  reducers: {
    resetAcademyState: (state) => {
      state = initialState
      return state
    },
    setActiveLesson: (state, action: PayloadAction<ProgramLesson | null>) => {
      state.activeLesson = action.payload
    },
    setShowPaymentForm: (state, action: PayloadAction<boolean>) => {
      state.showPaymentForm = action.payload
    },
    toggleShowPaymentForm: (state) => {
      state.showPaymentForm = !state.showPaymentForm
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getPrograms.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getPrograms.fulfilled, (state, action) => {
        state.isLoading = false
        state.programData = action.payload
      })
      .addCase(getPrograms.rejected, (state) => {
        state.isLoading = false
        state.programData = null
      })
      .addCase(getEnrollments.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getEnrollments.fulfilled, (state, action) => {
        state.isLoading = false
        state.enrollmentData = action.payload
      })
      .addCase(getEnrollments.rejected, (state) => {
        state.isLoading = false
        state.enrollmentData = null
      })
})

export const { resetAcademyState, setActiveLesson, setShowPaymentForm, toggleShowPaymentForm } = academySlice.actions

export default academySlice.reducer

export const getPrograms = createAsyncThunk('get-programs', async (_, { rejectWithValue }) => {
  try {
    const { data } = await railsApiService.apiInstance.get('/api/v2/academy/programs')

    // TODO: There's about 4000 enrollments here, check with backend team if we can remove this
    const normalizedData = (await normalizeJsonApi(data)) as ProgramData
    const filteredProgram: Record<string, ProgramValue> = {}
    Object.entries(normalizedData.program).forEach(([key, value]) => {
      const valueCopy = { ...value }

      delete valueCopy.relationships.enrollments

      filteredProgram[key] = valueCopy
    })

    return {
      ...normalizedData,
      program: filteredProgram
    }
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const getEnrollments = createAsyncThunk('get-enrollments', async (_, { rejectWithValue }) => {
  try {
    const { data } = await railsApiService.apiInstance.get('/api/v2/academy/enrollments')

    return (await normalizeJsonApi(data)) as EnrollmentData
  } catch (error) {
    return rejectWithValue(error)
  }
})

// Program types

export interface ProgramData {
  program: Record<string, ProgramValue>
  lesson: Record<string, ProgramLesson>
  product: Record<string, ProgramProduct>
  optionValue: Record<string, ProgramOptionValue>
  optionType: Record<string, ProgramOptionType>
  variant: Record<string, ProgramVariant>
}

export interface ProgramLesson {
  id: string
  type: ProgramDataType
  attributes: LessonAttributes
  relationships: LessonRelationships
}

export interface LessonAttributes {
  title: string
  description: string
  number: number
  duration: number
  videoDuration: number | null
  videoThumbnail: null | string
  previewMode: boolean
  videoPayload?: ProgramLessonVideoPayload
  survey: ProgramLessonSurveyData | null
}

export interface ProgramLessonSurveyData {
  id: number
  survey: ProgramLessonSurvey | string
  createdAt: string
  updatedAt: string
  surveyUuid: null | string
  contextType: string
  contextId: number
  surveyCommit: null | string
  surveyPath: null | string
}

export interface ProgramLessonSurvey {
  locale: string
  pages: Page[]
}

export interface Page {
  name: string
  elements: Element[]
}

export interface Element {
  type: ElementType
  name: string
  title: TitleData | string
  isRequired?: boolean
  choices?: Choice[]
}

export interface Choice {
  value: string
  text: string
}

export interface TitleData {
  default: string
  ptBr: string
}

export type ElementType = 'radiogroup' | 'comment'

export interface ProgramLessonVideoPayload {
  title: string
  description: string
  kind: string
  playlist: ProgramPlaylist[]
  feedInstanceId: string
}

export interface ProgramPlaylist {
  title: string
  mediaid: string
  link: string
  image: string
  images: ProgramPlaylistImage[]
  duration: number
  pubdate: number
  description: string
  sources: ProgramPlaylistSource[]
  tracks: ProgramPlaylistTrack[]
  variations: Variations
}

export interface ProgramPlaylistImage {
  src: string
  width: number
  type: string
}

export interface ProgramPlaylistSource {
  file: string
  type: SourceType
  height?: number
  width?: number
  label?: string
  bitrate?: number
  filesize?: number
  framerate?: number
}

export type SourceType = 'application/vnd.apple.mpegurl' | 'video/mp4' | 'audio/mp4'

export interface ProgramPlaylistTrack {
  file: string
  kind: string
}

export interface Variations {}

export interface LessonRelationships {
  program: ProgramOptionTypeData
}

export interface ProgramOptionTypeData {
  data: DAT
}

export interface DAT {
  id: string
  type: ProgramDataType
}

export type ProgramDataType =
  | 'program'
  | 'optionValue'
  | 'optionType'
  | 'variant'
  | 'taxon'
  | 'enrollment'
  | 'lesson'
  | 'product'

export interface ProgramOptionType {
  id: string
  type: ProgramDataType
  attributes: OptionTypeAttributes
  relationships: OptionTypeRelationships
}

export interface OptionTypeAttributes {
  name: string
  presentation: string
  position: number
}

export interface OptionTypeRelationships {
  optionValues: ProgramOptionValues
}

export interface ProgramOptionValues {
  data: DAT[]
}

export interface ProgramOptionValue {
  id: string
  type: ProgramDataType
  attributes: OptionTypeAttributes
  relationships: OptionValueRelationships
}

export interface OptionValueRelationships {
  optionType: ProgramOptionTypeData
}

export interface ProgramProduct {
  id: string
  type: ProgramDataType
  attributes: ProgramProductAttributes
  relationships: ProgramProductRelationships
}

export interface ProgramProductAttributes {
  name: string
  description: null
  price: string
  currency: string
  displayPrice: string
  availableOn: string
  slug: string
  metaDescription: null
  metaKeywords: null
  updatedAt: string
  purchasable: boolean
  inStock: boolean
  backorderable: boolean
}

export interface ProgramProductRelationships {
  variants: ProgramOptionValues
  optionTypes: ProgramOptionValues
  productProperties: ProgramOptionValues
  taxons: ProgramOptionValues
  images: ProgramOptionValues
  defaultVariant: ProgramOptionTypeData
  variantsIncludingMaster: ProgramOptionValues
}

export interface ProgramValue {
  id: string
  type: ProgramDataType
  attributes: ProgramAttributes
  relationships: ProgramRelationships
}

export interface ProgramAttributes {
  slug: string
  title: string
  description: string
  shortName: string
  videoPayload: ProgramVideoPayload
}

export interface ProgramVideoPayload {
  title?: string
  description?: string
  kind?: string
  playlist?: ProgramPlaylist[]
  feedInstanceId?: string
  message?: string
}

export interface ProgramRelationships {
  lessons: ProgramOptionValues
  enrollments?: ProgramOptionValues
  product: ProgramOptionTypeData
}

export interface ProgramVariant {
  id: string
  type: ProgramDataType
  attributes: ProgramVariantAttributes
  relationships: ProgramVariantRelationships
}

export interface ProgramVariantAttributes {
  name: string
  sku: string
  price: string
  currency: string
  displayPrice: string
  weight: string
  height: null
  width: null
  depth: null
  isMaster: boolean
  optionsText: string
  slug: string
  description: null
  purchasable: boolean
  inStock: boolean
  backorderable: boolean
}

export interface ProgramVariantRelationships {
  product: ProgramOptionTypeData
  images: ProgramOptionValues
  optionValues: ProgramOptionValues
}

// Enrollment types

export interface EnrollmentData {
  enrollment: Record<string, EnrollmentValue>
  cart: Record<string, StorefrontCart>
  attendance: Record<string, Attendance>
  enrollmentPayment: EnrollmentPaymentData
}

export interface Attendance {
  id: string
  type: string
  attributes: AttendanceAttributes
  relationships: AttendanceRelationships
}

export interface AttendanceAttributes {
  state: string
  unlockedAt: string
  startedAt: string
  sentEvaluationAt: null
  shouldEndAt: string
  evaluation: null
  videoPayload: VideoPayload
}

export interface VideoPayload {
  title: string
  description: string
  kind: string
  playlist: ProgramPlaylist[]
  feedInstanceId: string
}

export interface AttendanceRelationships {
  lesson: EnrollmentRelationship
  enrollment: EnrollmentRelationship
  survey: EnrollmentRelationship
}

export interface EnrollmentRelationship {
  data: DAT[] | DAT | null
}

export interface EnrollmentValue {
  id: string
  type: string
  attributes: EnrollmentAttributes
  relationships: EnrollmentRelationships
}

export interface EnrollmentAttributes {
  state: string
  isInterruptable: boolean
}

export interface EnrollmentRelationships {
  program: EnrollmentRelationship
  spreeOrder: EnrollmentRelationship
  enrollmentPayments: EnrollmentRelationship
  attendances: EnrollmentRelationship
}

export interface EnrollmentPaymentData {
  '2201': EnrollmentPayment
}

export interface EnrollmentPayment {
  id: string
  type: string
  attributes: EnrollmentPaymentAttributes
  relationships: EnrollmentPaymentRelationships
}

export interface EnrollmentPaymentAttributes {
  id: number
  state: string
  nextPaymentDueAt: null
}

export interface EnrollmentPaymentRelationships {
  spreeOrder: EnrollmentRelationship
}
